// UI language of editor-v2 (spec 2026-09-17 "Formular-Bearbeitung mehrsprachig").
//
// Deliberately not vue-i18n: the admin app runs vue-i18n 8 on Vue 2, and one
// package name cannot hold two major versions. Keys are nested JSON in
// ../locales. Lookup order: chosen language, then en-US, then the key itself,
// so a missing text shows up as its key instead of an empty label.
import { ref, type Ref } from 'vue';
import deDE from '../locales/de-DE.json';
import enUS from '../locales/en-US.json';

export type UiLocale = 'de-DE' | 'en-US';
export const UI_LOCALES: readonly UiLocale[] = ['de-DE', 'en-US'];
export const DEFAULT_UI_LOCALE: UiLocale = 'de-DE';
const FALLBACK_UI_LOCALE: UiLocale = 'en-US';

export type MessageNode = string | MessageTree;
export interface MessageTree { readonly [key: string]: MessageNode }
export type MessageParams = Readonly<Record<string, string | number>>;
export type MessageCatalog = Readonly<Record<UiLocale, MessageTree>>;

export interface Translator {
  locale: Ref<UiLocale>;
  setLocale(raw: string | null | undefined): void;
  t(key: string, params?: MessageParams): string;
  tc(key: string, count: number, params?: MessageParams): string;
}

// A text is the one node that is not an object (the files hold nothing else).
function isText(node: MessageNode): node is string {
  return !(node instanceof Object);
}

export function flattenMessages(tree: MessageTree, prefix = ''): Map<string, string> {
  const out = new Map<string, string>();
  for (const [key, node] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isText(node)) {
      out.set(path, node);
    } else {
      for (const [nested, text] of flattenMessages(node, path)) out.set(nested, text);
    }
  }
  return out;
}

export function toUiLocale(raw: string | null | undefined): UiLocale {
  return UI_LOCALES.find((candidate) => candidate === raw) ?? DEFAULT_UI_LOCALE;
}

// The admin passes its own UI language as ?uiLocale= on both iframe URLs.
export function localeFromSearch(search: string): UiLocale {
  return toUiLocale(new URLSearchParams(search).get('uiLocale'));
}

function fill(text: string, params: MessageParams | undefined): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params ? String(params[name]) : whole));
}

export function createTranslator(catalog: MessageCatalog, initial: UiLocale = DEFAULT_UI_LOCALE): Translator {
  const flat = new Map<UiLocale, Map<string, string>>();
  for (const code of UI_LOCALES) flat.set(code, flattenMessages(catalog[code]));
  const current = ref<UiLocale>(initial);

  function lookup(key: string): string {
    return flat.get(current.value)?.get(key) ?? flat.get(FALLBACK_UI_LOCALE)?.get(key) ?? key;
  }

  return {
    locale: current,
    setLocale(raw) { current.value = toUiLocale(raw); },
    t(key, params) { return fill(lookup(key), params); },
    tc(key, count, params) {
      return fill(lookup(`${key}.${count === 1 ? 'one' : 'other'}`), { ...params, count });
    },
  };
}

const translator = createTranslator({ 'de-DE': deDE, 'en-US': enUS });

export const { locale, setLocale, t, tc } = translator;
